import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TicketDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticket: {
    ticket_number: string;
    username: string;
    type: string;
    subject: string;
    description: string;
    status: string;
    created_at: string;
    attachments?: any;
  } | null;
}

const typeLabels: Record<string, string> = {
  request: 'Solicitação',
  suggestion: 'Sugestão',
  compliment: 'Elogio',
  complaint: 'Reclamação',
  report: 'Denúncia',
};

const statusLabels: Record<string, string> = {
  open: 'Aberto',
  in_progress: 'Em Andamento',
  resolved: 'Resolvido',
  closed: 'Fechado',
};

const TicketDetailsModal: React.FC<TicketDetailsModalProps> = ({
  isOpen,
  onClose,
  ticket,
}) => {
  if (!ticket) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-background border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            Ticket #{ticket.ticket_number}
            <Badge variant="outline" className="ml-2">
              {statusLabels[ticket.status] || ticket.status}
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Criado em {format(new Date(ticket.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", { locale: ptBR })}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Usuário</p>
            <p className="text-foreground">{ticket.username}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Tipo</p>
            <Badge variant="secondary">
              {typeLabels[ticket.type] || ticket.type}
            </Badge>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Assunto</p>
            <p className="text-foreground font-medium">{ticket.subject}</p>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground mb-1">Descrição</p>
            <div className="bg-muted/50 rounded-lg p-4 text-foreground whitespace-pre-wrap">
              {ticket.description}
            </div>
          </div>

          {ticket.attachments && Array.isArray(ticket.attachments) && ticket.attachments.length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Anexos</p>
              <div className="space-y-2">
                {ticket.attachments.map((attachment: string, index: number) => (
                  <a
                    key={index}
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-sm text-primary hover:underline"
                  >
                    Anexo {index + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TicketDetailsModal;
